import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckBox from "../../components/input/CheckBox";
import ResizableTextBox from "../../components/input/ResizableTextBox";
import SelectBox from "../../components/input/SelectBox";
import TextBox from "../../components/input/TextBox";
import { FormSection, FieldGrid } from "../../components/m3";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Corporate } from "../../db_access/corporate";
import { GHG_Assessment } from "../../db_access/ghg_assessment";
import { Structure } from "../../db_access/structure";
import { Supply_chain } from "../../db_access/supply_chain";
import { categoryToMethodologyMap } from "./ScopeCategory";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

export const staticReferences = [
  "ISO14025: 2006-Environmental labels and declarations -Type III environmental declarations -Principles and procedures",
  "ISO14040: 2006-Environmental management- Life cycle assessment -Principles and framework",
  "ISO14044: 2006/Amd 2: 2020 -Environmental management -Life cycle assessment -Requirements and guidelines",
  "ISO21930: 2017 Sustainability in buildings and civil engineering works -Core rules for environmental product declarations of construction products and services",
  "Part A: Life Cycle Assessment Calculation Rules and Report Requirements UL Environment (December 2018, version 3.2)",
  "Part B: Concrete Masonry and Segmental Concrete Paving Product EPD Requirements ULEnvironment (November 2020, v1.0)",
];
const boundary_tooltip =
  "Which consolidation approach was chosen (check each consolidation " +
  "approach for which your company is reporting emissions.) If your company is reporting according to more than one " +
  "consolidation approach, please complete and attach an additional completed reporting template that provides your " +
  "company’s emissions data following the other consolidation approach(es)";
const is_scope_3_included_tooltip = "Are Scope 3 emissions included in this inventory?";
const biogenic_emission_tooltip = "Direct CO2 emissions from Biogenic combustion (mtCO2)";
const base_year_tooltip = "Year chosen as base year";
const clarification_tooltip = "Clarification of company-determined policy for making base year emissions recalculations";
const context_tooltip = "Context for any significant emissions changes that trigger base year emissions recalculations";
const idling_tooltip = "Average vehicle idling percentage (%) used for SBTi estimation";
const fuel_economy_tooltip = "Average vehicle fuel economy used for SBTi estimation";

const OrganizationLifeCycleAssessment = ({ LCA_type, emissions, emissionDetails, ghg, readOnly = false, validationStatus = "Not Validated", structure = null }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { emissionDetailOptions, isSaveClicked, setIsSaveVisible, setRefresh, setIsSaveClicked, setIsLoading, selectedStructure } = useContextProvider();
  const { t } = useTranslation();
  const activeStructure = structure || selectedStructure;
  const typeStr = String(activeStructure?.type || "").toLowerCase().trim();
  const isTransport = Boolean(
    typeStr.includes("transport") ||
    typeStr.includes("courier") ||
    typeStr.includes("logistics")
  );
  const [localGHG, setLocalGHG] = useState({
    ...ghg,
    functional_unit: ghg?.functional_unit ?? null,
    average_idling_percentage: ghg?.average_idling_percentage ?? "",
    average_fuel_economy: ghg?.average_fuel_economy ?? "",
  });
  const [isModified, setIsModified] = useState(false);
  const equity_share = useMemo(() => ({ label: "Equity Share", value: "Equity Share" }), []);
  const financial_control = useMemo(() => ({ label: "Financial Control", value: "Financial Control" }), []);
  const operational_control = useMemo(() => ({ label: "Operational Control", value: "Operational Control" }), []);
  const units = useMemo(() => [
    { label: "kgCO2e", value: "kgCO2e" },
    { label: "tCO2e", value: "tCO2e" },
  ], []);

  const inputs = useMemo(
    () => [
      { label: "Primary Information", field: "primary_information", type: "text", colSpan: 2 },
      {
        label: "Organizational Boundaries",
        field: "boundary",
        type: "select",
        tooltip: boundary_tooltip,
        options: [equity_share, financial_control, operational_control],
        colSpan: 1
      },
      { label: "Base Year", field: "base_year", type: "number", tooltip: base_year_tooltip, colSpan: 1 },
      {
        label: "Average Idling Percentage (%)",
        field: "average_idling_percentage",
        type: "number",
        tooltip: idling_tooltip,
        colSpan: 1,
        showIf: () => isTransport,
      },
      {
        label: "Average Fuel Economy",
        field: "average_fuel_economy",
        type: "number",
        tooltip: fuel_economy_tooltip,
        colSpan: 1,
        showIf: () => isTransport,
      },
      // { label: "Biogenic Emission", field: "biogenic_emission", type: "number", tooltip: biogenic_emission_tooltip, colSpan: 1 },
      // { label: "Functional Unit", field: "functional_unit", type: "select", options: units, colSpan: 1, defaultValue: "kgCO2e" },
      { label: "Clarification", field: "clarification", type: "text", tooltip: clarification_tooltip, colSpan: 1 },
      { label: "Context", field: "context", type: "text", tooltip: context_tooltip, colSpan: 1 },
      { label: "Exclusion", field: "exclusion", type: "text", colSpan: 1 },
      { label: "Assumption", field: "assumption", type: "text", colSpan: 1 },
    ],
    [equity_share, financial_control, operational_control, units, isTransport]
  );
  const save = useCallback(() => {
    let udpateGHG = null;
    if (LCA_type === Supply_chain.label) {
      udpateGHG = GHG_Assessment.update_ghg_by_supply_chain;
    } else if (LCA_type === Corporate.label) {
      udpateGHG = GHG_Assessment.update_ghg_by_corporate;
    } else if (LCA_type === Structure.label) {
      udpateGHG = GHG_Assessment.update_ghg_by_structure;
    }
    if (udpateGHG) {
      udpateGHG(localGHG).finally(() => {
        setIsModified(false);
        setIsSaveClicked(false);
        setIsLoading(false);
        setRefresh((r) => !r);
      });
    }
  }, [LCA_type, localGHG, setIsModified, setIsSaveClicked, setIsLoading, setRefresh]);
  const formatReferences = useCallback(
    (emissionDetails) => {
      const formattedEmissionDetails = emissionDetails
        .map((ed) => {
          const emission = emissions.find((em) => em.id === ed.emission_id);
          const matches = emissionDetailOptions?.[ed.type]?.[ed.category]?.[ed.sub_category || ""]?.[ed.description];
          const firstMatch = matches && matches.length > 0 ? matches[0] : null;
          const reference = firstMatch ? firstMatch.reference : "No reference found";
          return `Emission Category: ${emission?.category || "N/A"}, Type: ${ed.type}, Category: ${ed.category}, Sub-Category: ${ed.sub_category}, Description: ${ed.description
            }, Reference: ${reference}`;
        })
        .filter(Boolean)
        .join("\n");
      return [...staticReferences, formattedEmissionDetails].map((item) => `${item}`).join("\n");
    },
    [emissionDetailOptions, emissions]
  );

  useEffect(() => {
    if (isModified) {
      setIsSaveVisible(true);
    }
  }, [isModified, setIsSaveVisible]);
  useEffect(() => {
    const ghgAssessmentInfo = { formattedEmissions: formatEmissions(emissions), formattedReferences: formatReferences(emissionDetails) };
    setLocalGHG((prev) => ({ ...prev, ...ghgAssessmentInfo }));
  }, [emissionDetails, emissions, formatReferences]);
  useEffect(() => {
    if (!isSaveClicked) return;
    if (!isModified) return;
    save();
  }, [isSaveClicked, isModified, save]);
  const formatEmissions = (emissions) => {
    return emissions
      .map((emission) => {
        const methodology = categoryToMethodologyMap[emission.category];
        return `Emission Category: ${emission.category || "N/A"}, Methodology: ${methodology}`;
      })
      .filter(Boolean)
      .join("\n");
  };

  useEffect(() => {
    setLocalGHG({
      ...ghg,
      functional_unit: ghg?.functional_unit ?? null,
      average_idling_percentage: ghg?.average_idling_percentage ?? "",
      average_fuel_economy: ghg?.average_fuel_economy ?? "",
    });
  }, [ghg]);

  return (
    <Box sx={{ "& h3, & p": { textAlign: "left" } }}>
      <FormSection
        title={t("GHG Emission Assessment")}
        description={t("Define organizational boundaries, base year, and contextual accounting policies")}
      >
        <FieldGrid columns={2}>
        {inputs.map((input, index) => {
          if (input.showIf && !input.showIf(localGHG)) return null;
          if (input.type === "text") {
            return (
              <Box key={index} sx={{ gridColumn: input.colSpan === 2 ? "1 / -1" : "auto" }}>
                <ResizableTextBox
                  label={input.label}
                  value={localGHG?.[input.field] ?? ""}
                  disabled={readOnly}
                  onChange={(e) => {
                    if (readOnly) return;
                    setLocalGHG((prev) => ({ ...prev, [input.field]: e.target.value }));
                    setIsModified(true);
                  }}
                  tooltip={input?.tooltip}
                />
              </Box>
            );
          } else if (input.type === "select") {
            return (
              <Box key={index} sx={{ gridColumn: input.colSpan === 2 ? "1 / -1" : "auto" }}>
                <SelectBox
                  label={input.label}
                  options={input.options}
                  value={localGHG?.[input.field] ?? input.defaultValue ?? null}
                  disabled={readOnly}
                  onChange={(e, newValue) => {
                    if (readOnly) return;
                    setLocalGHG((prev) => ({ ...prev, [input.field]: newValue.value }));
                    setIsModified(true);
                  }}
                  freeSolo={false}
                  tooltip={input?.tooltip}
                />
              </Box>
            );
          } else if (input.type === "number") {
            return (
              <Box key={index} sx={{ gridColumn: input.colSpan === 2 ? "1 / -1" : "auto" }}>
                <TextBox
                  label={input.label}
                  value={localGHG?.[input.field] || ""}
                  disabled={readOnly}
                  tooltip={input?.tooltip}
                  onChange={(e) => {
                    if (readOnly) return;
                    setLocalGHG((prev) => ({ ...prev, [input.field]: e.target.value }));
                    setIsModified(true);
                  }}
                />
              </Box>
            );
          }
          return null;
        })}
      </FieldGrid>
    </FormSection>
    </Box>
  );
};
export default OrganizationLifeCycleAssessment;
